import React from 'react';
import {
    Avatar,
    AvatarProps,
    Box,
    Chip,
    ChipProps,
    IconButton,
    Paper,
    Skeleton,
    SxProps,
    Theme,
    Tooltip,
    Typography,
    alpha,
    lighten,
    useTheme,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * The dashboard's one card language: shell, tinted header, icon tile, list
 * rows, chips, date tiles, empty states. Every dashboard card is built from
 * these pieces so their colours live in one place.
 *
 * Dark mode takes every colour from the theme (src/theme/refinedDark.ts):
 * semantic accents read `palette[key].main/.dark/.light`, neutrals read
 * `divider` and `text.primary`. Light mode keeps the dashboard's original
 * Tailwind-toned accents, declared once in ACCENTS below.
 */

type PaletteKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/** main: tile/fill colour, dark: gradient end, text: tone for text on a wash. */
interface Tones { main: string; dark: string; text: string }

type AccentSpec =
    /** A semantic accent: the theme palette in dark; `light` overrides it in light mode. */
    | { palette: PaletteKey; light?: Tones }
    /** A genuinely categorical hue with no semantic meaning; dark tones are derived from it. */
    | { categorical: Tones };

const ACCENTS = {
    primary: { palette: 'primary' },
    secondary: { palette: 'secondary' },
    success: { palette: 'success', light: { main: '#10b981', dark: '#059669', text: '#059669' } },
    warning: { palette: 'warning', light: { main: '#f59e0b', dark: '#d97706', text: '#b45309' } },
    error: { palette: 'error', light: { main: '#ef4444', dark: '#dc2626', text: '#dc2626' } },
    info: { palette: 'info', light: { main: '#3b82f6', dark: '#2563eb', text: '#3b82f6' } },
    /** "On leave": yellow beside "absent" red in light; the warning tone in dark. */
    caution: { palette: 'warning', light: { main: '#eab308', dark: '#ca8a04', text: '#ca8a04' } },
    teal: { categorical: { main: '#14b8a6', dark: '#0d9488', text: '#0f766e' } },
    purple: { categorical: { main: '#a855f7', dark: '#9333ea', text: '#9333ea' } },
    violet: { categorical: { main: '#8b5cf6', dark: '#7c3aed', text: '#7c3aed' } },
    orange: { categorical: { main: '#f97316', dark: '#ea580c', text: '#c2410c' } },
    cyan: { categorical: { main: '#06b6d4', dark: '#0891b2', text: '#0e7490' } },
} satisfies Record<string, AccentSpec>;

export type DashboardAccent = keyof typeof ACCENTS;

export interface ResolvedAccent extends Tones { isDark: boolean }

/** Resolves an accent to concrete colours for the current theme mode. */
export const useDashboardAccent = (accent: DashboardAccent): ResolvedAccent => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const spec: AccentSpec = ACCENTS[accent];
    if ('categorical' in spec) {
        const c = spec.categorical;
        return isDark ? { ...c, text: lighten(c.main, 0.45), isDark } : { ...c, isDark };
    }
    const p = theme.palette[spec.palette];
    if (isDark) return { main: p.main, dark: p.dark, text: p.light, isDark };
    return spec.light ? { ...spec.light, isDark } : { main: p.main, dark: p.dark, text: p.dark, isDark };
};

/** Neutral tones for inside dashboard cards, all derived from the theme's text colour. */
export const useDashboardTones = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const ink = (opacity: number) => alpha(theme.palette.text.primary, opacity);
    return {
        theme,
        isDark,
        ink,
        /** Card outline and the hairlines inside cards. */
        line: isDark ? theme.palette.divider : ink(0.06),
    };
};

/** A 4px scrollbar for the cards' scrolling lists. */
export const thinScrollbarSx = (thumb: string, thumbHover: string = thumb) => ({
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
        background: thumb,
        borderRadius: 2,
        '&:hover': { background: thumbHover },
    },
});

const toSxArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : [sx]);

interface DashboardCardProps {
    accent: DashboardAccent;
    icon: SvgIconComponent;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Right side of the header: count chips, "View all" links. */
    actions?: React.ReactNode;
    /** Pinned under the body, above a hairline. */
    footer?: React.ReactNode;
    children: React.ReactNode;
    sx?: SxProps<Theme>;
}

/** Card shell with a tinted header strip, gradient icon tile, title and optional subtitle. */
export const DashboardCard: React.FC<DashboardCardProps> = ({
    accent, icon: Icon, title, subtitle, actions, footer, children, sx,
}) => {
    const a = useDashboardAccent(accent);
    const { theme, isDark, line } = useDashboardTones();

    return (
        <Paper
            elevation={0}
            sx={[
                {
                    height: '100%',
                    border: '1px solid',
                    borderColor: line,
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    boxShadow: isDark ? 'none' : `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
                },
                ...toSxArray(sx),
            ]}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: subtitle ? 1.75 : 1.5,
                    // Dark: a neutral header; the accent lives on the icon tile alone.
                    background: isDark
                        ? alpha(theme.palette.text.primary, 0.02)
                        : `linear-gradient(135deg, ${alpha(a.main, 0.05)} 0%, ${alpha(a.dark, 0.05)} 100%)`,
                    borderBottom: '1px solid',
                    borderColor: isDark ? theme.palette.divider : alpha(a.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            borderRadius: 2,
                            background: isDark
                                ? `linear-gradient(135deg, ${alpha(a.main, 0.3)} 0%, ${alpha(a.dark, 0.2)} 100%)`
                                : `linear-gradient(135deg, ${a.main} 0%, ${a.dark} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isDark ? 'none' : `0 2px 8px ${alpha(a.main, 0.25)}`,
                        }}
                    >
                        <Icon sx={{ color: isDark ? a.text : theme.palette.common.white, fontSize: 18 }} />
                    </Box>
                    {subtitle ? (
                        <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ lineHeight: 1 }}>
                                {title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, opacity: 0.85 }}>
                                {subtitle}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body1" fontWeight={600}>
                            {title}
                        </Typography>
                    )}
                </Box>
                {actions}
            </Box>

            {children}

            {footer && (
                <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid', borderColor: line }}>
                    {footer}
                </Box>
            )}
        </Paper>
    );
};

/** Sizing for the full-body loading state of the teacher/employee panels. */
export const PANEL_SKELETON_SX = { p: 3, borderRadius: 3, height: '100%', minHeight: 350 };

/** The outlined paper that card skeletons sit in. */
export const DashboardSkeletonCard: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => {
    const { isDark, ink } = useDashboardTones();
    return (
        <Paper
            elevation={0}
            sx={[
                {
                    border: '1px solid',
                    borderColor: isDark ? 'divider' : ink(0.08),
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                },
                ...toSxArray(sx),
            ]}
        >
            {children}
        </Paper>
    );
};

/**
 * Loading placeholder for a list card. `subtitle` mirrors a header with icon
 * tile and caption; `rowLead` picks an avatar circle or a date-tile square.
 */
export const DashboardListCardSkeleton: React.FC<{
    subtitle?: boolean;
    rows?: number;
    rowLead?: 'avatar' | 'tile';
    sx?: SxProps<Theme>;
}> = ({ subtitle, rows = 3, rowLead = 'avatar', sx }) => (
    <DashboardSkeletonCard sx={sx}>
        <Box sx={{ px: 2, py: subtitle ? 1.5 : 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            {subtitle ? (
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Box flex={1}>
                        <Skeleton width="50%" height={16} />
                        <Skeleton width="65%" height={12} />
                    </Box>
                </Box>
            ) : (
                <Skeleton width={110} height={16} />
            )}
        </Box>
        <Box sx={{ p: 1.5 }}>
            {Array.from({ length: rows }, (_, i) => (
                <Box key={i} display="flex" alignItems="center" gap={rowLead === 'avatar' ? 1.5 : 1.25} mb={1}>
                    {rowLead === 'avatar'
                        ? <Skeleton variant="circular" width={32} height={32} />
                        : <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1.5 }} />}
                    <Box flex={1}>
                        <Skeleton width="60%" height={14} />
                        <Skeleton width="40%" height={12} />
                    </Box>
                </Box>
            ))}
        </Box>
    </DashboardSkeletonCard>
);

/** A softly filled, outlined list row. `interactive` adds the hover nudge. */
export const DashboardListRow: React.FC<{ children: React.ReactNode; interactive?: boolean; sx?: SxProps<Theme> }> = ({
    children, interactive, sx,
}) => {
    const { isDark, ink } = useDashboardTones();
    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: ink(isDark ? 0.02 : 0.015),
                    border: '1px solid',
                    borderColor: ink(0.04),
                },
                !!interactive && {
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        transform: 'translateX(2px)',
                        bgcolor: ink(isDark ? 0.04 : 0.025),
                        borderColor: ink(0.08),
                    },
                },
                ...toSxArray(sx),
            ]}
        >
            {children}
        </Box>
    );
};

type AccentChipProps = Omit<ChipProps, 'color' | 'variant'> & {
    accent: DashboardAccent;
    /** solid: filled in light, tinted in dark. soft: tinted wash. outlined: paper with a tinted outline in light. */
    variant?: 'solid' | 'soft' | 'outlined';
};

/** A small chip tinted with a dashboard accent. */
export const AccentChip: React.FC<AccentChipProps> = ({ accent, variant = 'soft', sx, ...rest }) => {
    const a = useDashboardAccent(accent);
    const { theme, isDark } = useDashboardTones();
    const look = {
        solid: {
            bgcolor: isDark ? alpha(a.main, 0.2) : a.main,
            color: isDark ? a.text : theme.palette.common.white,
        },
        soft: {
            bgcolor: alpha(a.main, isDark ? 0.16 : 0.12),
            color: a.text,
        },
        outlined: {
            bgcolor: isDark ? alpha(a.main, 0.2) : 'background.paper',
            color: isDark ? a.text : a.dark,
            border: isDark ? 'none' : `1px solid ${alpha(a.main, 0.2)}`,
        },
    }[variant];
    return <Chip size="small" {...rest} sx={[look, ...toSxArray(sx)]} />;
};

/** A 32px initial avatar tinted with a dashboard accent. */
export const AccentAvatar: React.FC<Omit<AvatarProps, 'color'> & { accent: DashboardAccent }> = ({ accent, sx, ...rest }) => {
    const a = useDashboardAccent(accent);
    return (
        <Avatar
            {...rest}
            sx={[
                {
                    width: 32,
                    height: 32,
                    bgcolor: alpha(a.main, a.isDark ? 0.3 : 0.2),
                    color: a.text,
                    fontSize: 13,
                    fontWeight: 500,
                },
                ...toSxArray(sx),
            ]}
        />
    );
};

/** A 40px day-and-month tile for dated list rows. */
export const DateTile: React.FC<{ accent: DashboardAccent; date: string }> = ({ accent, date }) => {
    const a = useDashboardAccent(accent);
    const d = new Date(date);
    return (
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: a.isDark
                    ? alpha(a.main, 0.15)
                    : `linear-gradient(135deg, ${alpha(a.main, 0.15)} 0%, ${alpha(a.dark, 0.1)} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Typography variant="caption" fontWeight={700} sx={{ color: a.text }} lineHeight={1} fontSize={13}>
                {d.getDate()}
            </Typography>
            <Typography variant="caption" sx={{ color: a.isDark ? a.main : a.dark }} fontSize={9} lineHeight={1}>
                {d.toLocaleDateString('en-US', { month: 'short' })}
            </Typography>
        </Box>
    );
};

interface DashboardEmptyStateProps {
    accent: DashboardAccent;
    icon: SvgIconComponent;
    title: React.ReactNode;
    description: React.ReactNode;
    /** compact: small tile inside a list area. roomy: large circle filling a card body. */
    size?: 'compact' | 'roomy';
    sx?: SxProps<Theme>;
}

/** The centred icon + message shown when a card has nothing to list. */
export const DashboardEmptyState: React.FC<DashboardEmptyStateProps> = ({
    accent, icon: Icon, title, description, size = 'compact', sx,
}) => {
    const a = useDashboardAccent(accent);
    const { isDark, ink } = useDashboardTones();

    if (size === 'roomy') {
        return (
            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 260,
                        textAlign: 'center',
                        px: 3,
                    },
                    ...toSxArray(sx),
                ]}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(a.main, isDark ? 0.15 : 0.08),
                        mb: 2,
                    }}
                >
                    <Icon sx={{ fontSize: 40, color: a.main, opacity: 0.7 }} />
                </Box>
                <Typography variant="h6" color="text.secondary" fontWeight={500} gutterBottom>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 250 }}>
                    {description}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    background: `radial-gradient(circle at 50% 50%, ${alpha(a.main, isDark ? 0.03 : 0.04)} 0%, transparent 70%)`,
                },
                ...toSxArray(sx),
            ]}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 3,
                    mb: 1.5,
                    background: alpha(a.main, isDark ? 0.08 : 0.06),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon sx={{ fontSize: 24, color: isDark ? alpha(a.main, 0.6) : alpha(a.main, 0.9) }} />
            </Box>
            <Typography color="text.secondary" variant="body2" fontWeight={500} sx={{ fontSize: 12, mb: 0.25 }}>
                {title}
            </Typography>
            <Typography variant="caption" sx={{ color: ink(0.35), fontSize: 10 }}>
                {description}
            </Typography>
        </Box>
    );
};

/** The "Refresh dashboard" icon button in each dashboard page header. */
export const DashboardRefreshButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { isDark, ink } = useDashboardTones();
    return (
        <Tooltip title="Refresh dashboard">
            <IconButton
                onClick={onClick}
                size="small"
                sx={{
                    bgcolor: ink(isDark ? 0.05 : 0.04),
                    '&:hover': { bgcolor: ink(isDark ? 0.1 : 0.08) },
                }}
            >
                <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
        </Tooltip>
    );
};
